import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Mail, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import OTPInput from '../components/OTPInput';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA challenge state — only set when the backend tells us 2FA is required.
  const [twoFactor, setTwoFactor] = useState(null); // { userId, email }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);

  const { login, verifyTwoFactor, resendTwoFactor } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password, rememberMe);
    if (result.success) {
      navigate('/catalog');
    } else if (result.twoFactorRequired) {
      setTwoFactor({ userId: result.userId, email: result.email });
      toast.success(`Verification code sent to ${result.email}`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setTwoFactorBusy(true);
    const r = await verifyTwoFactor(twoFactor.userId, twoFactorCode.trim(), rememberMe);
    if (r.success) navigate('/catalog');
    else setError(r.error);
    setTwoFactorBusy(false);
  };

  const handleResend = async () => {
    const r = await resendTwoFactor(twoFactor.userId);
    if (r.success) toast.success('New code sent to your email.');
    else toast.error(r.error || 'Could not resend code');
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <SEO title="Sign In" description="Sign in to your Wilvada account to view orders, manage addresses, redeem reward points, and access bulk pricing tiers." noIndex />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-heading text-2xl">{twoFactor ? 'Verify it\'s you' : 'Welcome back'}</CardTitle>
          <CardDescription className="font-body">
            {twoFactor
              ? <>We sent a 6-digit code to <strong>{twoFactor.email}</strong>. It expires in 10 minutes.</>
              : 'Sign in to your Wilvada account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4" data-testid="login-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {!twoFactor && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body">Email</Label>
                <Input id="email" type="email" placeholder="researcher@institution.edu" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="font-body">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="remember-me" checked={rememberMe} onCheckedChange={(c) => setRememberMe(c === true)} data-testid="login-remember-me" />
                <Label htmlFor="remember-me" className="font-body text-sm text-muted-foreground cursor-pointer">Remember me</Label>
              </div>
              <Button type="submit" className="w-full bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" disabled={loading} data-testid="login-submit-button">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}
          {twoFactor && (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="two-factor-form">
              <div className="rounded-md bg-[hsl(210,45%,95%)] border border-[hsl(210,45%,40%)]/30 p-3 text-xs text-foreground flex items-start gap-2">
                <Mail className="h-4 w-4 text-[hsl(210,45%,40%)] flex-shrink-0 mt-0.5" />
                <span>Check your email for the code. It may take a moment to arrive.</span>
              </div>
              <div className="space-y-3">
                <Label htmlFor="tfa-code" className="font-body block text-center">6-digit code</Label>
                <OTPInput value={twoFactorCode} onChange={setTwoFactorCode} testidPrefix="two-factor-code" />
              </div>
              <Button
                type="submit"
                className="w-full bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white"
                disabled={twoFactorBusy || twoFactorCode.length !== 6}
                data-testid="two-factor-verify-button"
              >
                {twoFactorBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : 'Verify & Sign In'}
              </Button>
              <div className="flex justify-between text-xs">
                <button type="button" onClick={handleResend} className="text-[hsl(210,45%,40%)] hover:underline" data-testid="two-factor-resend-button">Resend code</button>
                <button type="button" onClick={() => { setTwoFactor(null); setTwoFactorCode(''); }} className="text-muted-foreground hover:underline">Use a different account</button>
              </div>
            </form>
          )}
          {!twoFactor && (
            <p className="text-center text-sm text-muted-foreground mt-4 font-body">
              Don't have an account?{' '}
              <Link to="/register" className="text-foreground font-medium hover:underline" data-testid="register-link">Create one</Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
